import { GetterTree, MutationTree, ActionTree } from "vuex";
import { ApiProxy, ApiCallResult, PagedResult } from "@infoland/api-proxy";
import { PostFilter } from "../../../types/filter/postFilter";
import { ContentItemFilterField, ContentItemFilterFieldOption } from "../../../types/filter/contentItemFilterField";
import { PostFilterField } from "../../../types/filter/postFilterField";
import snackBarService from "../../../services/snackBarService";

//export type ActiveFilterFieldOptions = { [key: string]: Pick<PostFilterField, "filter_option_id" | "name"> };
type ActiveFilterFieldOption = ContentItemFilterFieldOption & {
    parentTreecode: string[];
};
export type ActiveFilterFieldOptions = { [key: string]: ActiveFilterFieldOption[] };

// state type
export interface ContentItemFilterState {
    filterFields: ContentItemFilterField[];
    activeFilterOptions: ActiveFilterFieldOptions;
    filter: PostFilter;
    filterId: string;
}

export function initialize(): ContentItemFilterState {
    return {
        filterFields: null,
        activeFilterOptions: {},
        filter: null,
        filterId: null
    };
}

// initial state
const state: ContentItemFilterState = initialize();

// getters for this store
const getters: GetterTree<ContentItemFilterState, any> = {
    /**
     * The last used filter
     * @param state The state to read from
     */
    filter(state: ContentItemFilterState): PostFilter {
        return state.filter;
    },

    /**
     * The filter ID
     * @param state state to read from
     */
    filterId(state: ContentItemFilterState): string {
        return state.filterId;
    },

    /**
     * The filter fields
     * @param state The state to read from
     */
    filterFields(state: ContentItemFilterState): ContentItemFilterField[] {
        return state.filterFields;
    },
    /**
     * The active filter options
     * @param state The state to read from
     */
    activeFilterOptions(state: ContentItemFilterState): ActiveFilterFieldOptions {
        return state.activeFilterOptions;
    }
};

// mutations for this store
const mutations: MutationTree<ContentItemFilterState> = {
    /* Set the last filter
     * @param state The state to write to
     * @param data the filter id and dto
     */
    filter(state: ContentItemFilterState, data: { id: string; filter: PostFilter }): void {
        if (data.id !== state.filterId) {
            //reset fields
            state.activeFilterOptions = {};
            state.filterFields = [];
        }

        state.filterId = data.id;
        state.filter = data.filter;
    },

    /**
     * Set the filter fields
     * @param state The state to write to
     * @param filterFields filter fields
     */
    filterFields(state: ContentItemFilterState, filterFields: ContentItemFilterField[]): void {
        state.filterFields = filterFields;
    },

    /**
     * Sets the active filter options
     * @param state The state to write to
     * @param filterOptions the active filter options
     */
    addActiveFilterOption(state: ContentItemFilterState, payload: { fieldId: string; option: ActiveFilterFieldOption }): void {
        state.activeFilterOptions[payload.fieldId] = [payload.option];
    },

    /**
     * removes the active filter option (and dependent options)
     */
    removeActiveFilterOption(state: ContentItemFilterState, fieldId: string): void {
        delete state.activeFilterOptions[fieldId];

        //check dependend options
        for (let index = 0; index < state.filterFields.length; index++) {
            const field = state.filterFields[index];
            if (field.dependent_on?.includes(parseInt(fieldId))) {
                delete state.activeFilterOptions[fieldId];
            }
        }
    },

    /**
     * Reset state to initial values
     * @param state current state
     */
    reset(state: ContentItemFilterState) {
        Object.assign(state, initialize());
    }
};

// actions for this store
const actions: ActionTree<ContentItemFilterState, any> = {
    async loadFilterFields({ commit }, filterId: string): Promise<void> {
        const path: string = `portals/content_items/filter/${filterId}/filter_fields`;
        let response = await ApiProxy.getRequest<ContentItemFilterField[]>(snackBarService.setMessage, path);
        if (response.statusCode === 200) {
            commit("filterFields", response.result);
        }
    },

    async postFilterFieldAsync({ commit, state }, payload: { fieldId: string; option: ContentItemFilterFieldOption }): Promise<boolean> {
        const path: string = `portals/content_items/filter/${state.filterId}/filter_fields/${payload.fieldId}`;

        let response = await ApiProxy.postRequest(snackBarService.setMessage, path, {
            filter_option_id: payload.option.filter_field_option_id
        });

        if (response.statusCode === 204) {
            // store activated option
            commit(`addActiveFilterOption`, payload);

            return true;
        }
        return false;
    },

    async deleteFilterFieldAsync({ commit, state }, fieldId: string): Promise<boolean> {
        const path: string = `portals/content_items/filter/${state.filterId}/filter_fields/${fieldId}`;
        let response: ApiCallResult<void> = await ApiProxy.deleteRequest(snackBarService.setMessage, path);
        if (response.statusCode === 204) {
            //local commit, don't use namespace
            commit(`removeActiveFilterOption`, fieldId);

            return true;
        }
        return false;
    }
};

// export the namespace
export const namespace = "contentItemsFilters";

// export the store 'module'
export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
